import {
  TYPE,
  useOperationsRpt,
  useOperationsRptExport,
} from '@services/report'
import Flex from '@shared/Flex'
import { Button } from '@shared/Form'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import Layout from '@shared/Layout'
import Table from '@shared/Table'
import { downloadFile, toThousandSeparator } from '@shared/utils/webHelper'
import { Form } from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { isNil } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'src/components/Form'
import OptionsSelect from 'src/components/OptionsSelect'
import { StatusBox } from '../style'

const initialReportType = {
  reportType: TYPE.YEAR,
  conditionDate: new Date().getFullYear(),
  startAt: moment().startOf('year').valueOf(),
  endAt: moment().endOf('year').valueOf(),
  pageSize: 999999999,
}

function Operations() {
  const { t } = useTranslation()

  const [isSearch, setIsSearch] = React.useState(false)

  const [form] = Form.useForm()
  const [currentType, setCurrentType] = React.useState(TYPE.YEAR)

  const { query, handleSearch, handleTableChange } = useSearch({
    form,
    initial: initialReportType,
    dateRangeField: ['startAt', 'endAt'],
  })

  const handleReportType = (val: any) => {
    setCurrentType(val?.reportType)
    return val
  }

  const [exporting, handleExport] = useOperationsRptExport(query, {
    onSuccess: (blob: any) => {
      downloadFile({
        blob,
        filename: 'OperationReport',
      })
    },
  })

  const { dataSource, loading } = useOperationsRpt(query)

  console.log('dataSource', dataSource)

  const columns: ColumnsType<any> = [
    {
      title: '縣市',
      dataIndex: 'cityName',
      align: 'center',
    },
    {
      title: '行政區',
      dataIndex: 'locationNs',
      align: 'center',
    },
    {
      title: '收益排名',
      dataIndex: 'rank',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'rank',
      }),
    },
    {
      title: '站點',
      width: 300,
      dataIndex: 'sessionName',
      align: 'center',
    },
    {
      title: '收益合計',
      dataIndex: 'totalIncome',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'totalIncome',
      }),
      render: v => toThousandSeparator(v),
    },
    {
      title: '充電費合計',
      dataIndex: 'chargeFee',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'chargeFee',
      }),
      render: v => toThousandSeparator(v),
    },
    {
      title: '停車費合計',
      dataIndex: 'parkingFee',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'parkingFee',
      }),
      render: v => toThousandSeparator(v),
    },
    {
      title: '占用費合計',
      dataIndex: 'occupationFee',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'occupationFee',
      }),
      render: v => toThousandSeparator(v),
    },
    {
      title: '廢單金額',
      dataIndex: 'cancelFee',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'cancelFee',
      }),
      render: v => toThousandSeparator(v),
    },
    {
      title: '用電量',
      dataIndex: 'chargeDegree',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '充電樁數',
      dataIndex: 'chargePointCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '快充數',
      dataIndex: 'dcChargingPointCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '慢充數',
      dataIndex: 'acChargingPointCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '納管車格數',
      dataIndex: 'parkingSpaceCount',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
  ]

  const renderStatusContent = () => {
    const data: any = dataSource
    return (
      <div className="flex">
        <StatusBox
          title="收益合計"
          unit="NTD"
          data={data}
          dataKey="totalIncome"
        />
        <StatusBox
          title="充電費合計"
          unit="NTD"
          data={data}
          dataKey="chargeFee"
        />
        <StatusBox
          title="停車費合計"
          unit="NTD"
          data={data}
          dataKey="parkingFee"
        />
        <StatusBox
          title="車格占用費合計"
          unit="NTD"
          data={data}
          dataKey="occupationFee"
        />
        <StatusBox
          title="廢單金額合計"
          unit="NTD"
          data={data}
          dataKey="cancelFee"
        />
        <StatusBox
          title="用電量合計"
          unit="kwh"
          data={data}
          dataKey="chargeDegree"
        />
        <StatusBox
          title="充電樁總數"
          data={data}
          dataKey="chargePointCount"
          detail={[
            {
              title: '快充',
              dataKey: 'dcChargingPointCount',
            },
            {
              title: '慢充',
              dataKey: 'acChargingPointCount',
            },
          ]}
        />
        <StatusBox
          title="總納管車格數"
          unit="格"
          data={data}
          dataKey="parkingSpaceCount"
        />
      </div>
    )
  }

  React.useEffect(() => {
    if (!isNil(query) && !isNil(query.reportType)) {
      setIsSearch(true)
    }
  }, [query])

  return (
    <>
      <Layout>
        <Form layout="inline" form={form}>
          <FormItem
            label={'縣市/行政區'}
            name="sessionId"
            className="w-[280px]">
            <OptionsSelect.CityAreaSelect
              className="w-130"
              supportAll={false}
            />
          </FormItem>
          <FormItem label={'營運商'} name="operator" className="w-130">
            <OptionsSelect.OperatorSelect
              className="w-130"
              allowClear
              supportAll
            />
          </FormItem>
          <FormItem
            label={`${t('report:report type')} / ${t('report:time')}`}
            name="reportTime"
            initialValue={initialReportType}
            className={currentType === TYPE.DAY ? 'w-[380px]' : 'w-[250px]'}
            getValueFromEvent={handleReportType}>
            <OptionsSelect.ReportRangeTimeSelect />
          </FormItem>

          <FormItem.Button>
            <Button
              loading={false}
              type="primary"
              className="w-96"
              onClick={handleSearch}>
              {t('common:search')}
            </Button>
          </FormItem.Button>
          <FormItem.Button>
            <Button
              loading={exporting}
              type="primary"
              ghost
              className="w-96"
              onClick={handleExport}>
              {t('report:export')}
            </Button>
          </FormItem.Button>
        </Form>

        {renderStatusContent()}
        {isSearch ? (
          <>
            <div className="mt-44 mb-16">
              <Flex between>
                <Table.RowCount count={dataSource?.content?.length || 0} />
              </Flex>
            </div>
            <Table
              simpleMode
              columns={columns}
              loading={loading}
              dataSource={dataSource || []}
              onChange={handleTableChange}
              pagination={false}
            />
          </>
        ) : (
          <div
            className="flex justify-center items-center text-center text-lg"
            style={{ color: '#8E8E8E', height: '50dvh', width: '50dvw' }}>
            <span>請先輸入搜尋條件範圍</span>
          </div>
        )}
      </Layout>
    </>
  )
}

export default React.memo(Operations)
